import { ExaminationSession } from "./examination-session.model";
import { IdentityVerification } from "./identity-verification.model";
import { Language } from "./language.model";
import { lilateGridVersion } from "./lilateGridVersion.model";
import { Paginator } from "./paginator.model";
import { ProctorScore } from "./proctoring.model";
import { StudentLight } from "./student.model";
import { Teacher } from "./teacher.model";
import { CandidatePath } from "./candidate-path.model";
import { ValidityWindowExtensionType } from "./service-validity.model";

/** Règle de début du LILATE, copiée à l'inscription Partenaire. */
export type LilateStartRule = 'end_of_training' | 'on_creation' | 'days_before_end';

/** Origine de la règle de début : surcharge de l'organisme ou réglage de l'entreprise. */
export type LilateStartRuleSource = 'organization' | 'entreprise';

export class Lilate {
    id: number;
    url: string;
    start: Date;
    end: Date;
    /** Heure du serveur au moment de la réponse (fiche seule) : recale l'horloge du poste, cf. ServerClockService. */
    server_now?: string;
    start_from?: Date | null;
    validity_period_from: Date;
    validity_period_to: Date;
    terminated_date: Date;
    expired_date: Date;
    /** Date de début du passage O, calculée par Engage (jour de Paris). */
    earliest_start_date?: string | null;
    /** Règle de début copiée à l'inscription ; null pour un LILATE créé hors Partenaire. */
    lilate_start_rule?: LilateStartRule | null;
    /** Nombre de jours avant la fin de formation (règle `days_before_end`). */
    lilate_start_rule_days?: number | null;
    lilate_start_rule_source?: LilateStartRuleSource | null;
    /** Plafond de la date butoir (jour de Paris). */
    validity_ceiling?: string | null;
    /** Action unique faite sur la validité (prolongation ou réactivation). */
    validity_window_extension_type?: ValidityWindowExtensionType | null;
    validity_window_extension_at?: string | null;
    /** Date butoir accordée par l'action ; le staff peut la raccourcir, jamais la dépasser. */
    validity_window_extension_granted_deadline?: string | null;
    status: LilateStatus;
    external_organisation_name: string;
    updated_at: Date;
    created_at: Date;
    creation_channel?: string | null;
    language: Language;
    student: StudentLight;
    targeted_level: string;
    teacher: Teacher;
    provider_link: string;
    is_student_provider_link_clicked: boolean;
    is_teacher_open_assessement_clicked: boolean;
    is_teacher_provider_link_clicked: boolean;
    invoice: string;
    last_partners_sync: Date;
    partners_lilate_demand_id: number;
    token_certificate: string;
    notes_to_teachers: string;
    identity_issue: boolean;
    version: lilateGridVersion;
    cancel_reason: string;
    cancel_reason_detail: string;
    examination_sessions: string[];
    examination_version: string;
    csat: number;
    nps: number;
    token_poll: string;
    level_global: any;
    examination_session_status: string;
    certification: string;
    niveau: string;
    in_training: Boolean;
    is_satisfaction_form_responded: Boolean;
    formation: string;
    /** Vrai si la formation associée au LILATE est certifiante (formation.certification_training). */
    formation_certification_training?: boolean;
    type_student: string;
    price_hour: number;
    mode_financement: string;
    partners_payment_status: string;
    can_choose_specific_examination: boolean;
    edof_id: string;
    sf_id: string;
    sf_object_id: string;
    sf_invoice_id: string;
    stripe_transaction_id: string;
    // Champs remboursement Stripe (alimentés par LilateRefundView, read-only côté front)
    stripe_refund_id?: string | null;
    refund_idempotency_key?: string | null;
    refunded_at?: string | null;
    // L'API renvoie l'URL hyperliée vers le User (HyperlinkedRelatedField),
    // pas un id numérique — d'où le typage `string | number | null`. Pour
    // l'affichage, préférer `refunded_by_name` (libellé "Prénom Nom").
    refunded_by?: string | number | null;
    refunded_by_name?: string | null;
    sf_credit_note_id?: string | null;
    status_export_certificat_cdc: string;
    cdc_technical_id: string;
    do_not_bill: boolean;
    last_sf_synced: Date;
    organization: string;
    company: string;
    specific_examination: string;
    invoice_organization: string;
    presence_check: boolean;
    minimum_access_time: number;
    is_archived: boolean;
    override_expired_date: Date;
    last_examination_session: ExaminationSession;
    
    // === NOUVEAUX CHAMPS MODALITÉ IA ===
    modality: 'examiner' | 'ai'; // Default: 'examiner'

    /** Parcours bundle lié (prérequis FlashLevel avant LILATE). */
    path?: CandidatePath | null;
    
    // Champs IA (nullable, renseignés uniquement si modality='ai')
    ai_test_plan_id?: number | null;
    ai_token?: string | null; // Token unique Rakam
    ai_prompt_context?: string | null;
    ai_prompt_type?: 'GENERIC' | 'CUSTOM' | null;
    ai_company_name?: string | null;
    ai_website?: string | null;
    ai_job?: string | null;
    ai_business?: string | null;
    ai_comfort_chips?: string[] | null;
    ai_comfort_details?: string | null;
    ai_camera_captures_expected?: number | null; // Nombre de captures attendues
    ai_level_global?: string | null; // Niveau CECRL (A0-C2)
    abandon_reason?: string | null; // Motif d'abandon (statut 'abandoned', modalité IA)
    done_at?: Date | null; // Date passage en 'done' (modalité IA uniquement)
    /**
     * Date de passage des documents (Lilate.exam_date) : fin réelle du test en IA,
     * créneau en examinateur. En IA, `start` est posé à la génération du test.
     * Lire via `lilateExamDate()`.
     */
    exam_date?: string | null;
    
    // Champs onboarding LILATE IA
    otp_verified_at?: Date | null; // Date vérification OTP
    legal_consents_accepted_at?: Date | null; // Date acceptation CGU/RGPD
    hardware_check_completed_at?: Date | null; // Date vérification matériel
    
    // Relations proctoring/identity (via GenericForeignKey côté back)
    proctor_score?: ProctorScore | null;
    identity_verification?: IdentityVerification | null;
}

export class LilateList extends Paginator {
    results: Lilate[];
    last?: Date;
    allids: number[];
}

export enum LilateStatus {
    pending_booking = "pending_booking",
    identity_issue = "identity_issue",
    booked = "booked",
    done = "done",
    cancelled_by_student = "cancelled_by_student",
    cancelled_by_teacher = "cancelled_by_teacher",
    cancelled_by_admin = "cancelled_by_admin",
    suspended = "suspended",
    abs_teacher_without_justification = "abs_teacher_without_justification",
    abs_student_without_justification = "abs_student_without_justification",
    expired = "expired",
    abandoned = "abandoned",
    // Nouveaux statuts pour modalité IA
    pending = "pending",  // En attente de passage (sans booking)
    ongoing = "ongoing",  // Test en cours
}

/**
 * Types de modalité LILATE
 */
export type LilateModality = 'examiner' | 'ai';

/**
 * Statuts spécifiques modalité IA
 */
export type LilateAiStatus = 
  | 'pending'    // En attente de passage
  | 'ongoing'    // Test en cours
  | 'done'       // Terminé
  | 'expired'    // Expiré
  | 'abandoned'  // Abandonné
  | 'cancelled_by_admin'; // Annulé
